import {format, startOfWeek, subDays} from 'date-fns';
import exportToCSV from "../utils/ExportExcel";
import { loadFromStorage, useSaveToStorage } from "../utils";
import { useState, useMemo } from "react"

import {useReactTable, getCoreRowModel, getSortedRowModel} from '@tanstack/react-table';

export const formatPlantValue = (value, type) => {
    if (value == null || value === '') return '';
    if (type === 'text') return String(value);
    if (type === 'pct') return `${(value * 100).toFixed(1)}%`;
    return Number(value).toLocaleString('es-MX', {maximumFractionDigits: type === 'int' ? 0 : 2});
}

export const usePlantFilters = (storageKey) => {
    const today = new Date();
    const maxDate = format(today, 'yyyy-MM-dd');
    const minDate = format(subDays(today, 7), 'yyyy-MM-dd');

    const persisted = loadFromStorage(storageKey) || {};
    const savedDay = persisted.fecha >= minDate && persisted.fecha <= maxDate ? persisted.fecha : null;
    const [fecha, setFecha] = useState(savedDay || format(subDays(today, 1), 'yyyy-MM-dd'));
    const [search, setSearch] = useState(persisted.search || "");
    const [sorting, setSorting] = useState(persisted.sorting || []);

    useSaveToStorage(storageKey, {fecha, search, sorting})

    return {fecha, minDate, maxDate, search, sorting, setFecha, setSearch, setSorting};
}

//Rows where any of the fields contains the search.
export const searchRows = (rows, search, fields) => {
    const term = search.toLowerCase();
    return term ? rows.filter(row => fields.some(field => String(row[field] ?? '').toLowerCase().includes(term))) : rows;
}

//Columns with their sort and format, and the table of a report.
export const usePlantTable = (columnDefs, rows, sorting, setSorting) => {
    const columns = useMemo(() => columnDefs.map(col => ({
        ...col,
        sortingFn: col.type === 'text' ? 'alphanumeric' : 'basic',
        cell: ({getValue}) => formatPlantValue(getValue(), col.type),
    })), [columnDefs]);

    const table = useReactTable({
        data: rows,
        columns,
        state: {sorting},
        onSortingChange: setSorting,
        enableSortingRemoval: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return {columns, table};
}

//Original rows of a table in the order they are sorted on screen
export const sortedRows = (table) => table.getSortedRowModel().rows.map(row => row.original);

export const exportPlantTables = (filename, blocks, styleCell) => {
    const csvRows = [];
    const cellInfo = []; //Per sheet row: its columns and original row (null in the headers and blank lines)

    blocks.forEach(({columns, rows, title}, i) => {
        if (i > 0) {
            csvRows.push([]);
            cellInfo.push(null);
        }
        csvRows.push(columns.map((col, c) => c === 0 && title ? title : col.header));
        cellInfo.push(null);
        rows.forEach(row => {
            csvRows.push(columns.map(col => row[col.accessorKey] ?? ""));
            cellInfo.push({columns, row});
        });
    });

    exportToCSV(filename, csvRows, (r, c) => {
        if (!cellInfo[r]) return;
        const {columns, row} = cellInfo[r];
        const style = {...(columns[c].type === 'pct' && typeof csvRows[r][c] === 'number' ? {numFmt: '0.0%'} : {}), ...styleCell?.(row, columns[c])};
        return Object.keys(style).length ? style : undefined;
    });
}

//Rates per machine and captura limits that applied to the week of a day: the copy saved for that week (or the latest before it) once the week is over, otherwise the current ones
export const getWeekThresholds = (percentages, day) => {
    const monday = startOfWeek(new Date(`${day}T00:00:00`), {weekStartsOn: 1});
    const isPreviousWeek = monday < startOfWeek(new Date(), {weekStartsOn: 1});
    const weekKey = `SEMANA#${format(monday, 'yyyy-MM-dd')}`;
    const saved = isPreviousWeek ? percentages.filter(p => p.machine.startsWith('SEMANA#') && p.machine <= weekKey).sort((a, b) => a.machine.localeCompare(b.machine)).pop() : null;
    return {
        rates: saved?.rates ?? Object.fromEntries(percentages.map(p => [p.machine, p.efficiency])),
        limits: saved ?? percentages.find(p => p.machine === 'CAPTURA'),
    };
}